/**
 * Protected Route Component
 * =============================================================================
 * Wrapper component that requires authentication to access a route
 *
 * Usage:
 *   <Route path="/protected" element={
 *     <ProtectedRoute>
 *       <ProtectedComponent />
 *     </ProtectedRoute>
 *   } />
 *
 * Features:
 * - Redirects to login if not authenticated
 * - Shows loading state while checking auth
 * - Preserves intended destination for post-login redirect
 */

import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../lib/logger';

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  logger.debug('🔒 ProtectedRoute →', {
    user: user?.email || 'NULL',
    loading
  });

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    console.log('🚫 → /login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // User is authenticated, render children
  console.log('✅ → children');
  return <>{children}</>;
};

export default ProtectedRoute;
